import './ForecastingPage.scss';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DeleteForecastArgs,
  ForecastGroup,
  ForecastId,
  getCombinedChannelPlatform,
  GetForecastDataParams,
  GetForecastDataResponse,
  GetForecastsParams,
  GetForecastsResponse,
  getKpiOutcomeString,
  OutcomeVariableType
} from '@blisspointmedia/bpm-types/dist/Forecasting';
import { SegmentedControlButton } from '@src/Components/SegmentedControlButton';
import { Button } from '../../Components/Button/Button';
import {
  Appearance,
  Background,
  BackgroundType,
  Design,
  DesignType,
  Placement,
  Size
} from '../../Components/designConstants';
import { Dropdown } from '../../Components/Dropdown';
import { Page } from '../../Components/Page';
import { PAGE_NAMES } from '../../Components/Page/page-constants';
import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import { useExperimentFlag } from '../../utils/experiment-context';
import { FetchError } from '../../utils/fetch-error';
import { awaitJSON, MetricsLambdaFetch } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useMap } from '../../utils/hooks/useData';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { BudgetBuilder } from './BudgetBuilder/BudgetBuilder';
import { ForecastDataQa } from './DataQa';
import {
  BudgetViewOptions,
  DataQaConfig,
  ForecastPropertyValueType,
  FullForecastSelection,
  MatchableForecastData,
  MatchableForecastOptions,
  NAVS,
  OUTCOME_VARIABLE_LABELS,
  PageTab,
  SelectableForecastProperty,
  SelectableForecastPropertyValue
} from './forecasting-constants';
import {
  buildForecastSelectionUrl,
  buildFullForecastSelection,
  buildGroupSelectionUrl,
  extractForecastValsFromQueryParams,
  extractGroupIdFromQueryParams,
  formatOutcomeLabel,
  getExactOrFirstForecastMatchInGroup,
  getForecastGroupLabels,
  getKpiOutcomeKeys,
  getKpiWithData,
  getSelectablePropertyFromForecast,
  getVisibleNavs,
  pairingForOutcome,
  removeForecastFromGroups
} from './forecasting-utils';
import { ForecastOnboarding } from './ForecastOnboarding';
import { ForecastResultsPage } from './ForecastResults/ForecastResultsPage';
import { RequestForecast } from './RequestForecast';
import { SavedForecasts } from './SavedForecasts/SavedForecasts';
import { ScalingResults } from './SaturationCurve/ScalingResults';
import { useForecastDataQa } from './useForecastDataQa';

const econometricsUsers = [
  'philip.rundall@tinuiti.com',
  'doug.macdonald@tinuiti.com',
  'taurey.carr@tinuiti.com',
  'will.hammond@tinuiti.com',
  'galen.murray@tinuiti.com',
  'morgan.stockham@tinuiti.com',
  'paul.sims@tinuiti.com',
  'christopher.nilsson@tinuiti.com'
];

const engineeringUsers = [
  'alex.echeverria@tinuiti.com',
  'automation@tinuiti.com',
  'benjamin.contesse@tinuiti.com',
  'berto.garciacarrillo@tinuiti.com',
  'brandon.huss@tinuiti.com',
  'carling.sugarman@tinuiti.com',
  'garrett.cheadle@tinuiti.com',
  'hinling.yeung@tinuiti.com',
  'jose.davilaciullo@tinuiti.com',
  'jp.walker@tinuiti.com',
  'justin.manus@tinuiti.com',
  'katya.villano@tinuiti.com',
  'keir.keenan@tinuiti.com',
  'kyle.chan@tinuiti.com',
  'miguel.garcia@tinuiti.com',
  'nathan.shields@tinuiti.com',
  'nicole.yu@tinuiti.com',
  'skye.bishop@tinuiti.com',
  'vinnie.jaidar@tinuiti.com',
  'wilson.huang@tinuiti.com',
  'wilson.reyhan@tinuiti.com'
];

interface SwitchableOptionProps {
  selectableForecastOptions: MatchableForecastOptions;
  fullSelectedForecast: FullForecastSelection;
  selectForecastWithNewProperty: <P extends SelectableForecastPropertyValue>(
    propertyName: P,
    value: ForecastPropertyValueType<P>
  ) => void;
  property: SelectableForecastPropertyValue;
  label: string;
  background?: BackgroundType;
  design?: DesignType;
  // Displays an option's raw value differently, e.g. groupId → date range text.
  formatOptionLabel?: (value: string) => string;
}

const SwitchableOption = ({
  selectableForecastOptions,
  fullSelectedForecast,
  property,
  selectForecastWithNewProperty,
  label,
  background = Background.LIGHT,
  design = Design.BRAND,
  formatOptionLabel
}: SwitchableOptionProps): ReactNode => {
  const selectableOptions = selectableForecastOptions[property];
  const currentValue = getSelectablePropertyFromForecast(fullSelectedForecast, property);
  const onChange = option => selectForecastWithNewProperty(property, option);

  const isOutcomeVariable = property === SelectableForecastProperty.OUTCOME_VARIABLE;
  const formatOutcomeVar = (value: string): string =>
    formatOutcomeLabel(fullSelectedForecast.kpiName, pairingForOutcome(value));

  const labelForValue = isOutcomeVariable ? formatOutcomeVar : formatOptionLabel;

  return selectableOptions.length > 0 ? (
    <Dropdown
      overlayPlacement={Placement.RIGHT}
      size={Size.LG}
      label={label}
      value={String(currentValue)}
      valueContent={
        labelForValue ? (
          <span className='dropdown-value'>{labelForValue(String(currentValue))}</span>
        ) : undefined
      }
      options={selectableOptions}
      onChange={onChange}
      formatOptionLabel={labelForValue ? option => labelForValue(option.value) : undefined}
    />
  ) : (
    <Button
      size={Size.LG}
      appearance={Appearance.EMPTY}
      background={background}
      design={design}
      className='forecasting-dropdown-without-options'
    >
      {label}: {labelForValue ? labelForValue(String(currentValue)) : currentValue}
    </Button>
  );
};

const ForecastingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // The groups and the company that owns their data (differs from cid on a test company, see
  // GetForecastsResponse) are one state value, so they always come from the same response.
  const [forecastsState, setForecastsState] = useState<
    { groups: ForecastGroup[]; dataCompany?: string } | undefined
  >();
  const forecastGroups = forecastsState?.groups;
  const dataCompany = forecastsState?.dataCompany;
  const [budgetView, setBudgetView] = useState<BudgetViewOptions>('showPercentage');
  const [dataQaConfig, setDataQaConfig] = useState<DataQaConfig>({
    channels: [],
    kpis: []
  });
  // Shared Data QA run selection across the Request Forecast and Data QA tabs.
  // Null = newest run (resolved off the single run list fetched below).
  const [selectedRunKey, setSelectedRunKey] = useState<string | null>(null);
  // Non-null once onboarding kicks off a run — drives the "run in progress" modal
  // on the Request Forecast tab (cleared on dismiss and by the effects below). Its
  // `id` is null when the lambda returned no parseable id, so the notice still
  // shows (sans id) on a successful save.
  const [inProgressRun, setInProgressRun] = useState<{ id: string | null } | null>(null);
  const [allForecastDataMap, setForecastData] = useMap<ForecastId, GetForecastDataResponse>({});
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const userEmail = useAppSelector(UserRedux.emailSelector);
  const userFullName = useAppSelector(UserRedux.fullNameSelector);
  const showWIPFeatures = false;
  const enableForecastingSelfServe = Boolean(useExperimentFlag('enableForecastingSelfServe'));
  const isInternalUser = Boolean(useAppSelector(UserRedux.isInternalSelector));
  const activeNavs = getVisibleNavs(NAVS, {
    selfServe: enableForecastingSelfServe,
    isInternal: isInternalUser
  });

  const { tab, goToTab } = useTabbedNav({
    baseURL: 'forecasting/',
    defaultKey: PageTab.FORECAST_RESULTS,
    navs: activeNavs.map(nav => nav.key as string)
  });

  // Both the Request Forecast form and the Data QA table read these runs. Fetch
  // once here (only while a QA tab is active) so switching between them reuses the
  // runs instead of each tab re-fetching from S3.
  const onQaTab = tab === PageTab.REQUEST_FORECAST || tab === PageTab.DATA_QA;
  const qaState = useForecastDataQa(onQaTab ? cid : undefined);
  const qaRuns = qaState.status === 'ready' ? qaState.runs : null;
  const selectedRun = qaRuns
    ? (qaRuns.find(run => run.s3Key === selectedRunKey) ?? qaRuns[0])
    : null;
  // Pin the shared selection to the newest run once runs load (and re-pin when a
  // client switch leaves the key stale) so a refresh that surfaces a newer run
  // doesn't silently swap the run under the Request Forecast form.
  useEffect(() => {
    if (qaRuns && !qaRuns.some(run => run.s3Key === selectedRunKey)) {
      setSelectedRunKey(qaRuns[0].s3Key);
    }
  }, [qaRuns, selectedRunKey]);
  // The "run in progress" notice is a one-shot: clear it when the user leaves the
  // Request Forecast tab (so it doesn't re-pop on return) or switches company (so
  // it can't show a prior client's run id) — both reachable via browser
  // back/forward, since the modal's backdrop dismisses any in-page click. Keyed on
  // the transition *away from* the tab (via a ref) so it never fires during the
  // onboarding → Request Forecast navigation that raises it.
  const prevTabRef = useRef(tab);
  useEffect(() => {
    if (prevTabRef.current === PageTab.REQUEST_FORECAST && tab !== PageTab.REQUEST_FORECAST) {
      setInProgressRun(null);
    }
    prevTabRef.current = tab;
  }, [tab]);
  useEffect(() => {
    setInProgressRun(null);
  }, [cid]);

  const canAddExistingForecast = useMemo(
    () => [...econometricsUsers, ...engineeringUsers].includes(userEmail),
    [userEmail]
  );

  const { forecastId, kpiName, outcomeVar } = useMemo(
    () => extractForecastValsFromQueryParams(location.search),
    [location.search]
  );
  // Budget Builder's own selection: a whole group, not one forecast (it still reads
  // the shared outcomeVar above as its Volume/Revenue display toggle).
  const budgetBuilderGroupId = useMemo(
    () => extractGroupIdFromQueryParams(location.search),
    [location.search]
  );

  // Resets on company change. For one render the state still holds the previous company's
  // groups and dataCompany, so that render's Budget Builder key is the previous company's
  // own (served from cache), never a mix, and the next render disables the query.
  useEffect(() => {
    setForecastsState(undefined);
  }, [cid]);

  // Cancelled on rerun/unmount so a stale company's late response can't clobber
  // the new company's state (mirrors useForecastDataQa's cancelled-flag pattern).
  useEffect(() => {
    if (!cid || forecastGroups) return undefined;
    let cancelled = false;
    const fetchForecastGroups = async () => {
      try {
        const params: GetForecastsParams = { company: cid };
        const res = await MetricsLambdaFetch('/getForecasts', {
          params: { ...params, testCompanyPageKey: 'FORECASTING' }
        });
        const resJSON = await awaitJSON<GetForecastsResponse>(res);
        if (!cancelled) {
          setForecastsState({
            groups: resJSON.forecastGroups,
            dataCompany: resJSON.dataCompany
          });
        }
      } catch (e) {
        if (!cancelled) {
          setForecastsState({ groups: [] });
          setError({ message: e.message, reportError: e });
        }
      }
    };
    fetchForecastGroups();
    return () => {
      cancelled = true;
    };
  }, [forecastGroups, cid, setError]);

  const handleDeleteForecast = useCallback(
    async (deletedForecastId: ForecastId) => {
      // `company` is this page's company: the lambda refuses a delete from a test company's page
      // (it lists DSW's forecasts) and one that doesn't own the forecast.
      const args: DeleteForecastArgs & { company: string } = {
        forecastId: deletedForecastId,
        user: { fullName: userFullName, email: userEmail },
        company: cid
      };
      await MetricsLambdaFetch('/deleteForecast', { method: 'DELETE', body: args });
      setForecastsState(
        prev =>
          prev && {
            ...prev,
            groups: removeForecastFromGroups(prev.groups, deletedForecastId) ?? []
          }
      );
      Mixpanel.track(MxE.RecordDeleted, { page: PAGE_NAMES.FORECASTING, entity: 'forecast' });
    },
    [userFullName, userEmail, cid]
  );

  // Load the saved forecasting scope (channels/KPIs the client chose during
  // onboarding) so the Request Forecast page can narrow its menu to it.
  useEffect(() => {
    if (!cid) return;
    const loadDataQaConfig = async () => {
      try {
        const res = await MetricsLambdaFetch('/loadDataQaConfig', {
          params: { company: cid, testCompanyPageKey: 'FORECASTING' }
        });
        const config = await awaitJSON<DataQaConfig>(res);
        setDataQaConfig(config);
      } catch (e) {
        // A client who hasn't onboarded yet has no saved config, so the endpoint
        // 404s. That's a benign cold start — keep the empty default so the Request
        // Forecast page falls back to the full QA universe. Only surface genuine
        // failures.
        if (e instanceof FetchError && e.status === 404) return;
        setError({ message: e.message, reportError: e });
      }
    };
    loadDataQaConfig();
  }, [cid, setError]);

  // Sorted so the date-range dropdown reads chronologically rather than in
  // whatever order the groups happened to come back from the API.
  const sortedForecastGroups = useMemo(
    () => [...(forecastGroups ?? [])].sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [forecastGroups]
  );

  useEffect(() => {
    if (tab !== PageTab.FORECAST_RESULTS || !forecastGroups) {
      return;
    }

    if (sortedForecastGroups.length < 1) {
      setError({ message: `No forecasts found for ${cid}` });
      return;
    }

    let selectedForecast;
    let selectedKpi;
    if (forecastId) {
      const forecastGroup = forecastGroups.find(group =>
        group.forecasts.find(f => f.id === forecastId)
      );

      if (!forecastGroup) {
        setError({ message: `No forecasts found with id ${forecastId}` });
        return;
      }

      // Still resolves and already fully selected -- nothing to default.
      if (kpiName && outcomeVar) {
        return;
      }

      selectedForecast = forecastGroup.forecasts.find(f => f.id === forecastId);
      [selectedKpi] = forecastGroup.kpis;
    } else {
      [selectedForecast] = sortedForecastGroups[0].forecasts;
      [selectedKpi] = sortedForecastGroups[0].kpis;
    }

    // A forecast that's still processing (or errored before producing any KPI
    // data) has no kpis yet -- nothing to default-select into the URL.
    if (!selectedForecast || !selectedKpi) {
      setError({ message: 'This forecast has no KPI data yet -- it may still be processing.' });
      return;
    }

    navigate(
      buildForecastSelectionUrl(location.pathname, {
        forecastId: selectedForecast.id,
        kpiName: selectedKpi.name,
        outcomeVar: selectedKpi.type
      })
    );
  }, [
    cid,
    forecastGroups,
    sortedForecastGroups,
    forecastId,
    kpiName,
    navigate,
    outcomeVar,
    setError,
    tab,
    location.pathname
  ]);

  // Default the Budget Builder tab to its first available group when no groupId is
  // selected yet, and surface an error if a groupId in the URL no longer exists.
  useEffect(() => {
    if (tab !== PageTab.BUDGET_BUILDER || !forecastGroups) {
      return;
    }

    if (sortedForecastGroups.length < 1) {
      setError({ message: `No forecasts found for ${cid}` });
      return;
    }

    if (budgetBuilderGroupId) {
      if (forecastGroups.some(group => group.groupId === budgetBuilderGroupId)) {
        return;
      }
      setError({ message: `No forecast group found with id ${budgetBuilderGroupId}` });
    }

    navigate(
      buildGroupSelectionUrl(location.pathname, sortedForecastGroups[0].groupId, outcomeVar)
    );
  }, [
    cid,
    forecastGroups,
    sortedForecastGroups,
    budgetBuilderGroupId,
    outcomeVar,
    navigate,
    setError,
    tab,
    location.pathname
  ]);

  const fullSelectedForecast: FullForecastSelection | undefined = useMemo(() => {
    if (!forecastGroups || !forecastId || !kpiName || !outcomeVar) {
      return undefined;
    }

    const forecastGroup = forecastGroups.find(group =>
      group.forecasts.find(f => f.id === forecastId)
    );

    if (!forecastGroup) {
      return undefined;
    }

    const forecast = forecastGroup.forecasts.find(f => f.id === forecastId);
    if (!forecast) {
      return undefined;
    }

    const kpi = forecastGroup.kpis.find(k => k.name === kpiName && k.type === outcomeVar);
    if (!kpi) {
      return undefined;
    }

    return buildFullForecastSelection(forecastGroup, forecast, kpi);
  }, [forecastGroups, forecastId, kpiName, outcomeVar]);

  const currentBudgetBuilderGroup = useMemo(
    () => forecastGroups?.find(group => group.groupId === budgetBuilderGroupId),
    [forecastGroups, budgetBuilderGroupId]
  );

  // Every forecast in the currently-selected group, for Budget Builder — a
  // group can hold multiple forecasts (one per channel/platform combo
  // submitted together), and each one contributes its own spend-group cards.
  const currentGroupForecasts = useMemo(
    () => currentBudgetBuilderGroup?.forecasts.map(f => ({ id: f.id, channel: f.channel })),
    [currentBudgetBuilderGroup]
  );

  useEffect(() => {
    const fetchForecastData = async (id: string) => {
      const params: GetForecastDataParams = { forecastId: id };

      const res = await MetricsLambdaFetch('/getForecastData', {
        params
      });
      const resJSON = await awaitJSON<GetForecastDataResponse>(res);
      setForecastData(id, resJSON);
    };

    if (forecastId && !allForecastDataMap[forecastId]) {
      fetchForecastData(forecastId);
    }
  }, [forecastId, allForecastDataMap, setForecastData]);

  const currentForecastData = forecastId ? allForecastDataMap[forecastId] : undefined;

  // The KPI/outcome pairs that actually have data for the current forecast --
  // undefined until that forecast's data has loaded. Drives both the picker
  // filtering below and the fallback effect that follows.
  const availableKpiOutcomeKeys = useMemo(
    () => (currentForecastData ? getKpiOutcomeKeys(currentForecastData) : undefined),
    [currentForecastData]
  );

  // Metadata can list a KPI/outcome pair the backend never produced data for; fall back to a sibling pair that has data, or error if none do.
  useEffect(() => {
    if (tab !== PageTab.FORECAST_RESULTS || !fullSelectedForecast || !availableKpiOutcomeKeys) {
      return;
    }

    const forecastGroup = forecastGroups?.find(
      group => group.groupId === fullSelectedForecast.groupId
    );
    const target = forecastGroup
      ? getKpiWithData(forecastGroup.kpis, availableKpiOutcomeKeys, {
          kpiName: fullSelectedForecast.kpiName,
          outcomeVar: fullSelectedForecast.outcomeVar
        })
      : undefined;

    if (!target) {
      setError({
        message: `No forecast data is available for any KPI on this forecast (${fullSelectedForecast.channel}).`
      });
      return;
    }

    if (
      target.kpiName === fullSelectedForecast.kpiName &&
      target.outcomeVar === fullSelectedForecast.outcomeVar
    ) {
      return;
    }

    navigate(
      buildForecastSelectionUrl(location.pathname, {
        forecastId: fullSelectedForecast.id,
        kpiName: target.kpiName,
        outcomeVar: target.outcomeVar
      })
    );
  }, [
    tab,
    fullSelectedForecast,
    availableKpiOutcomeKeys,
    forecastGroups,
    navigate,
    location.pathname,
    setError
  ]);

  const selectedForecastProperties: MatchableForecastData | undefined = useMemo(() => {
    if (!fullSelectedForecast || !forecastId || !outcomeVar || !kpiName) {
      return undefined;
    }

    const { channel, platform, groupId } = fullSelectedForecast;

    return {
      forecastId,
      [SelectableForecastProperty.CHANNEL_PLATFORM]: getCombinedChannelPlatform(channel, platform),
      [SelectableForecastProperty.OUTCOME_VARIABLE]: outcomeVar,
      [SelectableForecastProperty.KPI]: kpiName,
      [SelectableForecastProperty.FORECAST_GROUP]: groupId
    };
  }, [forecastId, fullSelectedForecast, kpiName, outcomeVar]);

  const forecastsMatchingAllButOne: MatchableForecastData[] = useMemo(() => {
    if (
      !forecastGroups ||
      !fullSelectedForecast ||
      !selectedForecastProperties ||
      !forecastId ||
      !outcomeVar ||
      !kpiName
    ) {
      return [];
    }

    const matchableForecasts: MatchableForecastData[] = forecastGroups.flatMap(group => {
      const { groupId } = group;
      return group.forecasts.flatMap(forecast => {
        const { channel, platform } = forecast;
        return group.kpis.flatMap(kpi => {
          const { type, name } = kpi;
          return {
            forecastId: forecast.id,
            [SelectableForecastProperty.CHANNEL_PLATFORM]: getCombinedChannelPlatform(
              channel,
              platform
            ),
            [SelectableForecastProperty.OUTCOME_VARIABLE]: type,
            [SelectableForecastProperty.KPI]: name,
            [SelectableForecastProperty.FORECAST_GROUP]: groupId
          };
        });
      });
    });

    const matchableForecastsByDiff: Record<string, MatchableForecastData[]> = {};

    matchableForecasts.forEach(potentialMatch => {
      let numDiff = 0;

      Object.values(SelectableForecastProperty).forEach(propKey => {
        if (potentialMatch[propKey] !== selectedForecastProperties[propKey]) {
          numDiff++;
        }
      });

      if (!matchableForecastsByDiff[String(numDiff)]) {
        matchableForecastsByDiff[String(numDiff)] = [];
      }

      matchableForecastsByDiff[String(numDiff)].push(potentialMatch);
    });

    return matchableForecastsByDiff['1'] ?? [];
  }, [
    forecastGroups,
    forecastId,
    fullSelectedForecast,
    kpiName,
    outcomeVar,
    selectedForecastProperties
  ]);

  const selectableForecastOptions: MatchableForecastOptions = useMemo(() => {
    const output = {};

    Object.values(SelectableForecastProperty).forEach(propKey => {
      // date-range is built below directly from sortedForecastGroups — every
      // saved group is a valid option, unlike the other switchers.
      if (propKey === SelectableForecastProperty.FORECAST_GROUP) return;

      // KPI/outcome switches never change forecastId, so availableKpiOutcomeKeys
      // (already loaded for it) can hide sibling pairs with no actual data.
      const isFilterableByData =
        propKey === SelectableForecastProperty.KPI ||
        propKey === SelectableForecastProperty.OUTCOME_VARIABLE;
      const candidates =
        isFilterableByData && availableKpiOutcomeKeys
          ? forecastsMatchingAllButOne.filter(match =>
              availableKpiOutcomeKeys.has(
                getKpiOutcomeString({
                  outcome: match[SelectableForecastProperty.OUTCOME_VARIABLE],
                  kpi: match[SelectableForecastProperty.KPI]
                })
              )
            )
          : forecastsMatchingAllButOne;

      const propSet = new Set(candidates.map(match => match[propKey]));
      if (selectedForecastProperties) {
        propSet.delete(selectedForecastProperties[propKey]);
      }
      output[propKey] = Array.from(propSet);
    });

    const groupIdSet = new Set(sortedForecastGroups.map(group => group.groupId));
    if (selectedForecastProperties) {
      groupIdSet.delete(selectedForecastProperties[SelectableForecastProperty.FORECAST_GROUP]);
    }
    output[SelectableForecastProperty.FORECAST_GROUP] = Array.from(groupIdSet);

    return output as MatchableForecastOptions;
  }, [
    sortedForecastGroups,
    forecastsMatchingAllButOne,
    selectedForecastProperties,
    availableKpiOutcomeKeys
  ]);

  // Maps each group's groupId back to its dropdown display text — see
  // getForecastGroupLabels for the disambiguation rules.
  const dateRangeLabelsByGroupId: Record<string, string> = useMemo(
    () => getForecastGroupLabels(sortedForecastGroups),
    [sortedForecastGroups]
  );

  const budgetBuilderGroupOptions = useMemo(
    () =>
      sortedForecastGroups.map(group => ({
        value: group.groupId,
        label: dateRangeLabelsByGroupId[group.groupId]
      })),
    [sortedForecastGroups, dateRangeLabelsByGroupId]
  );

  const onSelectBudgetBuilderGroup = useCallback(
    (newGroupId: string) =>
      navigate(buildGroupSelectionUrl(location.pathname, newGroupId, outcomeVar)),
    [navigate, location.pathname, outcomeVar]
  );

  const selectForecastWithNewProperty = useCallback(
    <P extends SelectableForecastPropertyValue>(
      propertyName: P,
      value: ForecastPropertyValueType<P>
    ) => {
      if (!selectedForecastProperties || !fullSelectedForecast) {
        console.error('selectedForecastProperties undefined in selectForecastWithNewProperty');
        return;
      }

      // The target group may not share the current channel/kpi/outcome, so find
      // the closest match instead of requiring an exact match like the others.
      if (propertyName === SelectableForecastProperty.FORECAST_GROUP) {
        const targetGroup = forecastGroups?.find(group => group.groupId === value);

        if (!targetGroup) {
          setError({ message: `Unable to locate a saved forecast for ${value}` });
          return;
        }

        const match = getExactOrFirstForecastMatchInGroup(targetGroup, fullSelectedForecast);
        if (!match) {
          setError({ message: `Saved forecast group ${targetGroup.groupId} has no forecasts` });
          return;
        }

        navigate(buildForecastSelectionUrl(location.pathname, match));
        return;
      }

      const foundMatch = forecastsMatchingAllButOne.find(potentialMatch =>
        Object.values(SelectableForecastProperty).reduce((isMatch, propKey) => {
          if (propKey === propertyName) {
            return potentialMatch[propKey] === value && isMatch;
          }

          return potentialMatch[propKey] === selectedForecastProperties[propKey] && isMatch;
        }, true)
      );

      if (foundMatch) {
        navigate(
          buildForecastSelectionUrl(location.pathname, {
            forecastId: foundMatch.forecastId,
            kpiName: foundMatch[SelectableForecastProperty.KPI],
            outcomeVar: foundMatch[SelectableForecastProperty.OUTCOME_VARIABLE]
          })
        );
      } else {
        setError({
          message: `Unable to locate a matching forecast with ${propertyName} = ${value}`
        });
      }
    },
    [
      forecastGroups,
      forecastsMatchingAllButOne,
      fullSelectedForecast,
      location.pathname,
      navigate,
      selectedForecastProperties,
      setError
    ]
  );

  const baseSwitchableOptionProps: Omit<
    SwitchableOptionProps,
    'property' | 'label' | 'background'
  > = useMemo(
    () => ({
      selectableForecastOptions,
      fullSelectedForecast: fullSelectedForecast as FullForecastSelection,
      selectForecastWithNewProperty
    }),
    [fullSelectedForecast, selectForecastWithNewProperty, selectableForecastOptions]
  );

  return (
    <Page
      title={PAGE_NAMES.FORECASTING}
      pageType={PAGE_NAMES.FORECASTING}
      navs={activeNavs}
      selectedNav={tab}
      onNav={key => {
        goToTab(key);
        Mixpanel.track(MxE.TabChange, { page: PAGE_NAMES.FORECASTING, option_selected: key });
      }}
      actions={
        tab === PageTab.FORECAST_RESULTS &&
        fullSelectedForecast && (
          <div className='forecasting-actions'>
            <SwitchableOption
              property={SelectableForecastProperty.FORECAST_GROUP}
              label='Forecast dates'
              background={Background.DARK}
              design={Design.NEUTRAL}
              formatOptionLabel={groupId => dateRangeLabelsByGroupId[groupId] ?? groupId}
              {...baseSwitchableOptionProps}
            />
          </div>
        )
      }
      subHeader={
        ([PageTab.FORECAST_RESULTS, PageTab.BUDGET_BUILDER] as string[]).includes(tab) && (
          <div className='forecasting-subheader'>
            {tab === PageTab.FORECAST_RESULTS && fullSelectedForecast && (
              <>
                <SwitchableOption
                  property={SelectableForecastProperty.CHANNEL_PLATFORM}
                  label='Channel'
                  {...baseSwitchableOptionProps}
                />
                <SwitchableOption
                  property={SelectableForecastProperty.KPI}
                  label='KPI'
                  {...baseSwitchableOptionProps}
                />
                <SwitchableOption
                  property={SelectableForecastProperty.OUTCOME_VARIABLE}
                  label='Outcome Variable'
                  {...baseSwitchableOptionProps}
                />
                {showWIPFeatures && (
                  <SegmentedControlButton
                    size={Size.LG}
                    className='forecasts-budget-display-toggle'
                    design={Design.NEUTRAL}
                    options={[
                      { key: 'showPercentage', label: 'Show % Budgets' },
                      { key: 'showDollars', label: 'Show $ Budgets' }
                    ]}
                    selectedOption={budgetView}
                    onChange={option => setBudgetView(option)}
                  />
                )}
              </>
            )}
            {tab === PageTab.BUDGET_BUILDER && budgetBuilderGroupId && (
              <SegmentedControlButton
                size={Size.LG}
                className='forecasts-outcome-toggle'
                design={Design.NEUTRAL}
                options={[
                  { key: 'Volume', label: OUTCOME_VARIABLE_LABELS.Volume },
                  { key: 'Revenue', label: OUTCOME_VARIABLE_LABELS.Revenue }
                ]}
                selectedOption={outcomeVar ?? 'Volume'}
                onChange={(option: OutcomeVariableType) =>
                  navigate(buildGroupSelectionUrl(location.pathname, budgetBuilderGroupId, option))
                }
              />
            )}
          </div>
        )
      }
    >
      <div className='full-page-router forecasting-page' data-testid='forecasting-page'>
        {tab === PageTab.FORECAST_ONBOARDING && enableForecastingSelfServe && (
          <ForecastOnboarding
            submitDataQaConfig={(channels: string[], kpis: string[], dataQaId?: string) => {
              setDataQaConfig({ channels, kpis });
              setInProgressRun({ id: dataQaId ?? null });
              goToTab(PageTab.REQUEST_FORECAST);
            }}
            savedScope={dataQaConfig}
          />
        )}
        {tab === PageTab.REQUEST_FORECAST && (
          <RequestForecast
            savedScope={dataQaConfig}
            goToTab={goToTab}
            qaState={qaState}
            selectedRun={selectedRun}
            onSelectRun={setSelectedRunKey}
            inProgressRun={inProgressRun}
            onDismissInProgress={() => setInProgressRun(null)}
          />
        )}
        {tab === PageTab.DATA_QA && (
          <ForecastDataQa
            savedScope={dataQaConfig}
            goToTab={goToTab}
            qaState={qaState}
            selectedRun={selectedRun}
            onSelectRun={setSelectedRunKey}
          />
        )}
        {tab === PageTab.SCALING_RESULTS && (
          <ScalingResults
            cid={cid}
            onGoToRequestForecast={() => goToTab(PageTab.REQUEST_FORECAST)}
          />
        )}
        {/* Every tab not listed here falls through to Forecast Results. */}
        {tab !== PageTab.SAVED_FORECASTS &&
          tab !== PageTab.BUDGET_BUILDER &&
          tab !== PageTab.FORECAST_ONBOARDING &&
          tab !== PageTab.REQUEST_FORECAST &&
          tab !== PageTab.DATA_QA &&
          tab !== PageTab.SCALING_RESULTS && (
            <ForecastResultsPage
              forecast={fullSelectedForecast}
              forecastData={forecastId ? allForecastDataMap[forecastId] : undefined}
              budgetSelectableOptions={[]}
              selectNewBudget={() => console.warn('TODO DEV-19729')}
              cid={cid}
            />
          )}
        {tab === PageTab.SAVED_FORECASTS && isInternalUser && (
          <SavedForecasts
            cid={cid}
            forecastGroups={forecastGroups}
            budgetView={budgetView}
            setBudgetView={setBudgetView}
            setError={setError}
            canAddExistingForecast={canAddExistingForecast}
            onDeleteForecast={handleDeleteForecast}
            showWIPFeatures={showWIPFeatures}
            goToTab={goToTab}
          />
        )}
        {tab === PageTab.BUDGET_BUILDER && (
          <BudgetBuilder
            outcomeVar={outcomeVar}
            period={
              currentBudgetBuilderGroup
                ? {
                    start: currentBudgetBuilderGroup.startDate,
                    end: currentBudgetBuilderGroup.endDate
                  }
                : undefined
            }
            forecasts={currentGroupForecasts}
            dataCompany={dataCompany}
            forecastGroupsLoading={!forecastGroups}
            groupOptions={budgetBuilderGroupOptions}
            selectedGroupId={budgetBuilderGroupId}
            onSelectGroup={onSelectBudgetBuilderGroup}
          />
        )}
      </div>
    </Page>
  );
};

export { ForecastingPage };
